import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import { makeStyles } from '@material-ui/core/styles';

import i18n from 'i18n-js';
import { formatAmount } from 'utils';
import { Card } from 'components/UI';
import { abilityConfig } from 'config/ability';
import { EditAccountModal } from 'components/Modals';
import { CustomersAction } from 'state/modules/customers';

const accountBoxSx = {
  backgroundColor: 'rgba(244,245,247,.4)',
  borderRadius: '4px',
  margin: '10px 0',
  padding: '8px 16px',
};

const accountListSx = {
  '& .MuiListItem-root': {
    padding: '5px 5px 5px 0',
  },
  '& .MuiListItemSecondaryAction-root': {
    right: 0,
  },
};

const listTextSx = {
  '& .MuiListItemText-primary': {
    fontSize: '0.75rem',
    fontWeight: 'bold',
    lineHeight: 1,
  },
};

const listItemsSx = {
  maxWidth: '40%',
  minWidth: '50px',
  wordBreak: 'break-all',
  textAlign: 'end',
};

// The shared Card (components/UI) is still MUI v4 and forwards `contentRootClasses`
// to CardContent as `classes`, so it needs a class name, not a style object.
const useCardContentStyles = makeStyles({
  root: {
    paddingTop: '24px',
  },
});

const Accounts = () => {
  const dispatch = useDispatch();
  const cardContentClasses = useCardContentStyles();

  const { account, credits } = useSelector((state) => state.customers || {});
  const { user } = useSelector((state) => state.users);
  const [openAccountModal, setOpenAccountModal] = useState(false);

  /**
   * Handle open edit account modal
   */
  const handleModalOpen = () => {
    setOpenAccountModal((prevState) => !prevState);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('payments.edit_account'),
      onClick: () => handleModalOpen(),
      can: { action: 'update', name: 'account' },
    },
  ];

  /**
   * Get total of amount issued
   * Based on user credits
   * @param {array} credits
   */
  const getTotalAmountIssued = (credits) => {
    let amountIssued = 0;
    if (credits.length > 0) {
      const filteredCredits = credits
        .filter((credit) => credit.is_valid)
        .map((credit) => credit.amount_issued);

      if (filteredCredits.length > 0) {
        amountIssued = filteredCredits.reduce((prev, next) => prev + next);
      }
    }
    return amountIssued;
  };

  /**
   * Get total of amount consumed
   * Based on user credits
   * @param {array} credits
   */
  const getTotalAmountConsumed = () => {
    let amountConsumed = 0;
    if (credits.length > 0) {
      const filteredConsumedCredits = credits
        .filter((credit) => credit.is_valid)
        .map((credit) => credit.amount_consumed);
      if (filteredConsumedCredits.length > 0) {
        amountConsumed = filteredConsumedCredits.reduce((prev, next) => prev + next);
      }
    }
    return amountConsumed;
  };

  const parseAccountData = () => {
    const pastDue = ((account?.past_due || 0) / 100).toFixed(2);
    const paidCredits = ((account?.paid_credit || 0) / 100).toFixed(2);
    const paidCard = ((account?.paid_card || 0) / 100).toFixed(2);
    const taxPaid = ((account?.tax || 0) / 100).toFixed(2);
    const invoiced = ((account?.invoiced || 0) / 100).toFixed(2);
    const spendingLimit = ((account?.spending_limit || 0) / 100).toFixed(2);
    const creditsAvailable = formatAmount(
      getTotalAmountIssued(credits) - getTotalAmountConsumed(credits)
    );

    const accountInfo = {
      paids: [
        { label: i18n.t('payments.paid_with_card'), value: paidCard || '-' },
        { label: i18n.t('payments.paid_with_credits'), value: paidCredits || '-' },
      ],
      totals: [
        { label: i18n.t('payments.total_past_due'), value: pastDue || '-' },
        { label: i18n.t('payments.tax_invoiced'), value: taxPaid || '-' },
        { label: i18n.t('payments.total_invoiced'), value: invoiced || '-' },
      ],
      credits: [{ label: i18n.t('payments.credits_available'), value: creditsAvailable || '-' }],
    };

    if (abilityConfig.can('enabled', 'subscription')) {
      accountInfo.credits.push({
        label: i18n.t('payments.spending_limit'),
        value: spendingLimit || '-',
        show: true,
      });
    }
    return accountInfo;
  };

  useEffect(() => {
    if (user?.account_id) {
      dispatch(CustomersAction.fetchAccountById(user.account_id));
    }
  }, [user, dispatch]);

  return (
    <Card
      menuItems={items}
      headerActionType="vertical"
      title={i18n.t('payments.account')}
      contentRootClasses={cardContentClasses}
    >
      <Box display="flex" justifyContent="space-between" mb={1}>
        <Typography>
          <Box fontSize={14} fontWeight="bold" component="span">
            {i18n.t('payments.account')} ID {account?.id || ''}
          </Box>
        </Typography>
      </Box>
      {Object.values(parseAccountData()).map((items, index) => (
        <Box sx={accountBoxSx} key={index}>
          <List component="nav" aria-labelledby="list" sx={accountListSx}>
            {items.map((item, itemIndex) => (
              <ListItem key={itemIndex}>
                <ListItemText sx={listTextSx} primary={item.label} />
                <ListItemSecondaryAction sx={listItemsSx}>
                  <Box fontSize={13}>$ {item.value}</Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
      {openAccountModal && (
        <EditAccountModal
          account={account}
          open={openAccountModal}
          handleClick={() => handleModalOpen()}
        />
      )}
    </Card>
  );
};

export default Accounts;
